import { apiClient } from '../utils/apiClient';
import { subdomainDetector } from '../utils/subdomainDetector';

export interface NotificationChannel {
  id: string;
  name: string;
  channel_type: 'email' | 'sms' | 'whatsapp';
  provider: string;
  configuration: Record<string, any>;
  is_active: boolean;
  is_default: boolean;
  rate_limit_per_minute?: number;
  rate_limit_per_hour?: number;
  rate_limit_per_day?: number;
  health_check_url?: string;
  last_health_check?: string;
  health_status?: 'healthy' | 'unhealthy' | 'unknown';
  created_at: string;
  updated_at: string;
}

export interface EnterpriseNotificationSettings {
  id: string;
  email_notifications_enabled: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_use_tls: boolean;
  smtp_username: string;
  smtp_password: string;
  from_email: string;
  sms_notifications_enabled: boolean;
  sms_provider: string;
  created_at: string;
  updated_at: string;
}

export interface TestChannelRequest {
  recipient: string;
  message?: string;
  configuration?: Record<string, any>;
}

export interface TestChannelResponse {
  message: string;
  message_sid?: string;
}

class NotificationService {
  private get baseEndpoint(): string {
    const context = subdomainDetector.getSubdomainInfo();
    console.log('🔗 NotificationService - getting endpoint for context:', context);
    
    if (context.isEnterprise) {
      console.log('🏢 NotificationService - using enterprise organization endpoint');
      return '/v1/platform/organization/notifications';
    } else {
      console.log('🏠 NotificationService - using tenant endpoint');
      return '/v1/core/api/notifications/channels';
    }
  }

  async getChannels() {
    const context = subdomainDetector.getSubdomainInfo();
    if (context.isEnterprise) {
      // Enterprise: Get organization notification settings
      return apiClient.get<any>(`${this.baseEndpoint}/`);
    } else {
      // Tenant: Get notification channels
      return apiClient.get<NotificationChannel[]>(`${this.baseEndpoint}/`);
    }
  }

  async getChannelsByType(type: 'email' | 'sms' | 'whatsapp') {
    const context = subdomainDetector.getSubdomainInfo();
    if (context.isEnterprise) {
      // Enterprise: Get organization notification settings (contains all types)
      const response = await apiClient.get<any>(`${this.baseEndpoint}/`);
      console.log('🏢 Enterprise notification settings response:', response);
      
      // Convert organization settings to channel-like format for compatibility
      if (response.data) {
        const settings = response.data;
        // Return as array with single item for compatibility with existing component logic
        return {
          data: [settings]
        };
      } else {
        return { data: [] };
      }
    } else {
      // Tenant: Get channels by type
      return apiClient.get<NotificationChannel[]>(`${this.baseEndpoint}/?channel_type=${type}`);
    }
  }

  async getChannel(id: string) {
    const context = subdomainDetector.getSubdomainInfo();
    if (context.isEnterprise) {
      // Enterprise: Get organization notification settings by ID
      return apiClient.get<any>(`${this.baseEndpoint}/${id}/`);
    } else {
      // Tenant: Get specific channel
      return apiClient.get<NotificationChannel>(`${this.baseEndpoint}/${id}/`);
    }
  }

  async createChannel(data: Partial<NotificationChannel>) {
    const context = subdomainDetector.getSubdomainInfo();
    if (context.isEnterprise) {
      // Enterprise: Create/update organization notification settings
      return apiClient.post<EnterpriseNotificationSettings>(`${this.baseEndpoint}/`, data);
    } else {
      // Tenant: Create channel
      return apiClient.post<NotificationChannel>(`${this.baseEndpoint}/`, data);
    }
  }

  async updateChannel(id: string, data: Partial<NotificationChannel>) {
    const context = subdomainDetector.getSubdomainInfo();
    if (context.isEnterprise) {
      // Enterprise: Update organization notification settings
      return apiClient.put<EnterpriseNotificationSettings>(`${this.baseEndpoint}/${id}/`, data);
    } else {
      // Tenant: Update channel
      return apiClient.put<NotificationChannel>(`${this.baseEndpoint}/${id}/`, data);
    }
  }

  // Enterprise-specific methods
  async createEnterpriseSettings(data: Partial<EnterpriseNotificationSettings>) {
    return apiClient.post<EnterpriseNotificationSettings>(`${this.baseEndpoint}/`, data);
  }

  async updateEnterpriseSettings(id: string, data: Partial<EnterpriseNotificationSettings>) {
    console.log('🔄 PUT request to update enterprise settings:', {
      url: `${this.baseEndpoint}/${id}/`,
      data: JSON.stringify(data, null, 2)
    });
    try {
      const response = await apiClient.put<EnterpriseNotificationSettings>(`${this.baseEndpoint}/${id}/`, data);
      console.log('✅ PUT request successful:', response);
      return response;
    } catch (error: any) {
      console.error('❌ PUT request failed:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error status text:', error.response?.statusText);
      throw error;
    }
  }

  async deleteChannel(id: string) {
    return apiClient.delete(`${this.baseEndpoint}/${id}/`);
  }

  async testChannel(id: string, testData: TestChannelRequest) {
    return apiClient.post<TestChannelResponse>(`${this.baseEndpoint}/${id}/test/`, testData);
  }

  async toggleChannelActive(id: string) {
    return apiClient.post<NotificationChannel>(`${this.baseEndpoint}/${id}/toggle_active/`);
  }

  // Helper methods for specific channel types
  async getEmailChannels() {
    return this.getChannelsByType('email');
  }

  async getSMSChannels() {
    return this.getChannelsByType('sms');
  }

  async getWhatsAppChannels() {
    return this.getChannelsByType('whatsapp');
  }

  // Create or update email channel - ensure only ONE email channel per tenant/enterprise
  async saveEmailChannel(data: {
    name?: string;
    mail_driver: string;
    host: string;
    port: number;
    username: string;
    password: string;
    encryption: 'tls' | 'ssl' | 'none';
    from_address: string;
    from_name: string;
    is_active: boolean;
  }, id?: string) {
    console.log('NotificationService.saveEmailChannel called with ID:', id);
    console.log('Email channel data:', data);
    
    const context = subdomainDetector.getSubdomainInfo();
    
    if (context.isEnterprise) {
      // Enterprise: Save to organization notification settings
      const enterpriseData = {
        email_notifications_enabled: data.is_active,
        smtp_host: data.host,
        smtp_port: data.port,
        smtp_use_tls: data.encryption === 'tls',
        smtp_username: data.username,
        smtp_password: data.password,
        from_email: data.from_address,
      };
      
      console.log('🏢 Saving enterprise notification settings:', enterpriseData);
      
      // For enterprise, we always update (it's a singleton)
      try {
        // First, get the current settings to get the ID
        const currentSettings = await apiClient.get<any>(`${this.baseEndpoint}/`);
        console.log('Current enterprise settings:', currentSettings);
        
        if (currentSettings.data && currentSettings.data.length > 0) {
          const settingsId = currentSettings.data[0].id;
          console.log('Updating enterprise settings with ID:', settingsId);
          return this.updateEnterpriseSettings(settingsId, enterpriseData);
        } else {
          console.log('Creating new enterprise settings');
          return this.createEnterpriseSettings(enterpriseData);
        }
      } catch (error) {
        console.error('Error accessing enterprise settings:', error);
        return this.createEnterpriseSettings(enterpriseData);
      }
    } else {
      // Tenant: Use the existing channel logic
      const channelData = {
        name: data.name || `Enterprise Email (${data.mail_driver})`,
        channel_type: 'email' as const,
        provider: data.mail_driver,
        configuration: {
          mail_driver: data.mail_driver,
          host: data.host,
          port: data.port,
          username: data.username,
          password: data.password,
          encryption: data.encryption,
          from_address: data.from_address,
          from_name: data.from_name,
        },
        is_active: data.is_active,
      };

      console.log('🏠 Saving tenant channel data:', channelData);

      // If we have an ID, try to update that specific channel
      if (id) {
        console.log('Updating specific channel:', id);
        return this.updateChannel(id, channelData);
      }
      
      // Otherwise, get existing email channels to find one to update
      try {
        const existingChannels = await this.getEmailChannels();
        const existingChannel = existingChannels.data?.[0]; // Should be only one
        
        if (existingChannel) {
          console.log('Updating existing channel:', existingChannel.id);
          return this.updateChannel(existingChannel.id, channelData);
        } else {
          console.log('Creating new channel (none exists)');
          return this.createChannel(channelData);
        }
      } catch (error) {
        console.error('Error fetching existing channels, creating new one:', error);
        return this.createChannel(channelData);
      }
    }
  }

  // Create or update SMS channel
  async saveSMSChannel(data: {
    name?: string;
    provider: string;
    account_sid: string;
    auth_token: string;
    from_number: string;
    webhook_url?: string;
    gateway_url?: string;
    gateway_port?: string;
    gateway_model?: string;
    sim_slot?: string;
    http_method?: string;
    content_type?: string;
    custom_parameters?: string;
    // Network routing fields
    sms_network_mode?: string;
    sms_lan_gateway_url?: string;
    sms_wan_gateway_url?: string;
    sms_auto_discovery_enabled?: boolean;
    sms_connection_timeout?: number;
    sms_endpoint_test_interval?: number;
    is_active: boolean;
  }, id?: string) {
    console.log('NotificationService.saveSMSChannel called with ID:', id);
    console.log('SMS channel data:', data);
    
    const context = subdomainDetector.getSubdomainInfo();
    
    if (context.isEnterprise) {
      // Enterprise: Save to organization notification settings
      const enterpriseData: any = {
        sms_notifications_enabled: data.is_active,
        sms_provider: data.provider,
        sms_from_number: data.from_number,
        sms_gateway_url: data.gateway_url || '',
        sms_gateway_port: data.gateway_port || '',
        sms_gateway_model: data.gateway_model || '',
        sms_sim_slot: data.sim_slot || '',
        sms_http_method: data.http_method || 'POST',
        sms_content_type: data.content_type || 'application/json',
        sms_custom_parameters: data.custom_parameters || '',
        // Network routing configuration
        sms_network_mode: data.sms_network_mode || 'auto',
        sms_lan_gateway_url: data.sms_lan_gateway_url || '',
        sms_wan_gateway_url: data.sms_wan_gateway_url || '',
        sms_auto_discovery_enabled: data.sms_auto_discovery_enabled !== undefined ? data.sms_auto_discovery_enabled : true,
        sms_connection_timeout: data.sms_connection_timeout || 10,
        sms_endpoint_test_interval: data.sms_endpoint_test_interval || 300,
      };
      
      // Only include credentials if they're not masked placeholders
      if (data.account_sid && data.account_sid !== '••••••••') {
        enterpriseData.sms_api_key = data.account_sid;
      }
      if (data.auth_token && data.auth_token !== '••••••••') {
        enterpriseData.sms_api_secret = data.auth_token;
      }
      
      console.log('Enterprise SMS data to save:', enterpriseData);
      console.log('🔍 Detailed enterprise data object:', JSON.stringify(enterpriseData, null, 2));
      
      // For enterprise, we always update (it's a singleton)
      try {
        // First, get the current settings to get the ID
        const currentSettings = await apiClient.get<any>(`${this.baseEndpoint}/`);
        console.log('Current enterprise settings:', currentSettings);
        console.log('Current settings data type:', typeof currentSettings.data);
        console.log('Current settings data:', currentSettings.data);
        
        // Handle both array and object responses
        let settingsId = null;
        if (currentSettings.data) {
          if (Array.isArray(currentSettings.data) && currentSettings.data.length > 0) {
            settingsId = currentSettings.data[0].id;
            console.log('Found settings ID from array:', settingsId);
          } else if (currentSettings.data.id) {
            settingsId = currentSettings.data.id;
            console.log('Found settings ID from object:', settingsId);
          }
        }
        
        if (settingsId) {
          console.log('Updating enterprise settings with ID:', settingsId);
          return this.updateEnterpriseSettings(settingsId, enterpriseData);
        } else {
          console.log('Creating new enterprise settings');
          return this.createEnterpriseSettings(enterpriseData);
        }
      } catch (error) {
        console.error('Error accessing enterprise settings:', error);
        throw error;
      }
    } else {
      // Tenant: Use channel API
      const channelData = {
        name: data.name || `SMS (${data.provider})`,
        channel_type: 'sms' as const,
        provider: data.provider,
        configuration: {
          provider: data.provider,
          account_sid: data.account_sid,
          auth_token: data.auth_token,
          from_number: data.from_number,
          webhook_url: data.webhook_url,
          gateway_url: data.gateway_url,
          gateway_port: data.gateway_port,
          gateway_model: data.gateway_model,
          sim_slot: data.sim_slot,
          http_method: data.http_method,
          content_type: data.content_type,
          custom_parameters: data.custom_parameters,
          // Network routing configuration
          sms_network_mode: data.sms_network_mode || 'auto',
          sms_lan_gateway_url: data.sms_lan_gateway_url || '',
          sms_wan_gateway_url: data.sms_wan_gateway_url || '',
          sms_auto_discovery_enabled: data.sms_auto_discovery_enabled !== undefined ? data.sms_auto_discovery_enabled : true,
          sms_connection_timeout: data.sms_connection_timeout || 10,
          sms_endpoint_test_interval: data.sms_endpoint_test_interval || 300,
        },
        is_active: data.is_active,
      };

      if (id) {
        return this.updateChannel(id, channelData);
      } else {
        return this.createChannel(channelData);
      }
    }
  }

  // Create or update WhatsApp channel
  async saveWhatsAppChannel(data: {
    name?: string;
    provider: string;
    account_sid: string;
    auth_token: string;
    from_number: string;
    business_name: string;
    webhook_url?: string;
    is_active: boolean;
  }, id?: string) {
    const channelData = {
      name: data.name || `WhatsApp (${data.provider})`,
      channel_type: 'whatsapp' as const,
      provider: data.provider,
      configuration: {
        provider: data.provider,
        account_sid: data.account_sid,
        auth_token: data.auth_token,
        from_number: data.from_number,
        business_name: data.business_name,
        webhook_url: data.webhook_url,
      },
      is_active: data.is_active,
    };

    if (id) {
      return this.updateChannel(id, channelData);
    } else {
      return this.createChannel(channelData);
    }
  }

  // Test email configuration
  async testEmail(channelId: string, testEmail: string, configuration?: Record<string, any>) {
    const context = subdomainDetector.getSubdomainInfo();
    
    if (context.isEnterprise) {
      // Enterprise: Use the organization notification settings test endpoint
      try {
        console.log('🧪 Testing email for enterprise with ID:', channelId);
        return await apiClient.post(`${this.baseEndpoint}/${channelId}/test/`, {
          recipient: testEmail,
          message: 'This is a test email from LabBoxx LIMS notification system.',
          configuration,
        });
      } catch (error: any) {
        console.error('❌ Enterprise email test failed:', error);
        throw error;
      }
    } else {
      // Tenant: Use the standard test channel endpoint
      return this.testChannel(channelId, {
        recipient: testEmail,
        message: 'This is a test email from LabBoxx LIMS notification system.',
        configuration,
      });
    }
  }

  // Test SMS configuration
  async testSMS(channelId: string, testPhone: string, configuration?: Record<string, any>) {
    const context = subdomainDetector.getSubdomainInfo();
    
    if (context.isEnterprise) {
      // Enterprise: Use test_sms endpoint
      try {
        console.log('🧪 Testing SMS for enterprise...');
        // First get the current settings to get the ID
        const currentSettings = await apiClient.get<any>(`${this.baseEndpoint}/`);
        console.log('🧪 Current settings for test SMS:', currentSettings.data);
        
        // Handle singleton response - data is the settings object directly, not an array
        let settingsId;
        if (currentSettings.data) {
          if (Array.isArray(currentSettings.data) && currentSettings.data.length > 0) {
            // Array format (fallback)
            settingsId = currentSettings.data[0].id;
            console.log('🧪 Found settings ID from array:', settingsId);
          } else if (currentSettings.data.id) {
            // Singleton format (expected)
            settingsId = currentSettings.data.id;
            console.log('🧪 Found settings ID from object:', settingsId);
          }
        }
        
        if (settingsId) {
          const testUrl = `${this.baseEndpoint}/${settingsId}/test_sms/`;
          console.log('🧪 Test SMS URL:', testUrl);
          const testData = {
            recipient: testPhone,
            message: 'This is a test SMS from SaaSBOXX Enterprise Platform.',
            configuration,
          };
          console.log('🧪 Test SMS payload:', testData);
          
          try {
            const result = await apiClient.post<any>(testUrl, testData);
            console.log('🧪 Test SMS success result:', result);
            return result;
          } catch (error) {
            console.error('🧪 Test SMS API error:', error);
            throw error;
          }
        } else {
          console.error('🧪 No settings ID found for test SMS');
          throw new Error('No enterprise settings found');
        }
      } catch (error) {
        console.error('Enterprise SMS test failed:', error);
        throw error;
      }
    } else {
      // Tenant: Use regular testChannel method
      return this.testChannel(channelId, {
        recipient: testPhone,
        message: 'This is a test SMS from LabBoxx LIMS notification system.',
        configuration,
      });
    }
  }

  // Test WhatsApp configuration
  async testWhatsApp(channelId: string, testPhone: string, configuration?: Record<string, any>) {
    return this.testChannel(channelId, {
      recipient: testPhone,
      message: 'This is a test WhatsApp message from LabBoxx LIMS notification system.',
      configuration,
    });
  }

  // SMS Network Management Methods
  async getSMSNetworkConfiguration() {
    const context = subdomainDetector.getSubdomainInfo();
    if (context.isEnterprise) {
      return apiClient.get<any>(`/v1/platform/organization/sms-network/configuration/`);
    } else {
      return apiClient.get<any>(`/v1/core/api/sms-network/configuration/`);
    }
  }

  async updateSMSNetworkConfiguration(data: any) {
    const context = subdomainDetector.getSubdomainInfo();
    if (context.isEnterprise) {
      return apiClient.post<any>(`/v1/platform/organization/sms-network/configuration/`, data);
    } else {
      return apiClient.post<any>(`/v1/core/api/sms-network/configuration/`, data);
    }
  }

  async testSMSEndpoints() {
    const context = subdomainDetector.getSubdomainInfo();
    if (context.isEnterprise) {
      return apiClient.post<any>(`/v1/platform/organization/sms-network/test-endpoints/`);
    } else {
      return apiClient.post<any>(`/v1/core/api/sms-network/test-endpoints/`);
    }
  }

  async discoverSMSEndpoints() {
    const context = subdomainDetector.getSubdomainInfo();
    if (context.isEnterprise) {
      return apiClient.post<any>(`/v1/platform/organization/sms-network/discover-endpoints/`);
    } else {
      return apiClient.post<any>(`/v1/core/api/sms-network/discover-endpoints/`);
    }
  }
}

export const notificationService = new NotificationService();